'use client';

import Link from 'next/link';
import { ArrowLeftIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { getRoute, useLocaleNavigation } from '@/lib/navigation';
import { notFound } from 'next/navigation';
import { Locale } from '@/constants/locales';
import { 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Badge, 
  Container, 
  Typography 
} from '@/components/design-system';

interface BlogLayoutProps {
    children: React.ReactNode;
    locale: Locale;
    slug: string;
}

export default function BlogLayout({
    children,
    locale,
    slug,
}: BlogLayoutProps) {
    const { getNavigationPath } = useLocaleNavigation();
    const t = useTranslations('blog');

    const [, route] = getRoute(slug, locale, { fullMatch: false });

    if (!route) {
        return notFound();
    }

    if (route?.pageType !== 'blog') {
        return notFound();
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
            {/* Header Section */}
            <div className="bg-white/80 backdrop-blur-sm border-b border-primary-200/50">
                <Container size="lg">
                    <div className="py-8">
                        {/* Back Button */}
                        <Link
                            href={getNavigationPath('blog')}
                            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors mb-6 group"
                        >
                            <ArrowLeftIcon className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                            <Typography variant="small" className="font-medium">
                                {t('layout.backToBlog')}
                            </Typography>
                        </Link>

                        {/* Category Badge */}
                        <div className="mb-4">
                            <Badge variant="secondary" className="bg-primary-100 text-primary-700 border-primary-200">
                                {(route as any).postData.categories[locale]}
                            </Badge>
                        </div>

                        {/* Title */}
                        <Typography 
                            variant="h1" 
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 mb-6 leading-tight"
                        >
                            {route.meta.title[locale] || ''}
                        </Typography>

                        {/* Meta Information */}
                        <div className="flex flex-wrap items-center gap-6 text-neutral-600 mb-6">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-primary-500" />
                                <Typography variant="small" className="text-neutral-600">
                                    {new Date((route as any).postData.date).toLocaleDateString(locale, {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </Typography>
                            </div>
                            <div className="flex items-center gap-2">
                                <ClockIcon className="w-4 h-4 text-primary-500" />
                                <Typography variant="small" className="text-neutral-600">
                                    {(route as any).postData.readTime[locale]}
                                </Typography>
                            </div>
                        </div>

                        {/* Tags */}
                        {(route.meta.tags[locale] || []).length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {(route.meta.tags[locale] || []).map((tag: string) => (
                                    <Badge
                                        key={tag}
                                        variant="outline"
                                        className="text-neutral-500 bg-neutral-50 border-neutral-200"
                                    >
                                        #{tag}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </Container>
            </div>

            {/* Hero Image */}
            {((route as any).postData.imageUrl) && (
                <Container size="lg" className="py-8">
                    <div className="h-64 md:h-80 lg:h-96 overflow-hidden rounded-2xl shadow-xl border border-primary-200/20">
                        <img 
                            src={(route as any).postData.imageUrl} 
                            alt={route.meta.title[locale] || ''}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </Container>
            )}

            {/* Content Section */}
            <Container size="lg" className="py-0">
                <Card variant="elevated" padding="lg" className="bg-white/90 backdrop-blur-sm border-primary-200/30">
                    <CardContent className="prose prose-lg max-w-none prose-headings:text-neutral-900 prose-p:text-neutral-700 prose-a:text-primary-600 prose-strong:text-neutral-900 prose-code:bg-neutral-100 prose-code:text-neutral-800 prose-pre:bg-neutral-900 prose-pre:text-neutral-100">
                        {children}
                    </CardContent>
                </Card>

                {/* Navigation */}
                <div className="mt-12 text-center">
                    <Button 
                        asChild
                        size="lg"
                        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                        <Link href={getNavigationPath('blog')}>
                            <ArrowLeftIcon className="w-4 h-4 mr-2" />
                            {t('layout.backToAllPosts')}
                        </Link>
                    </Button>
                </div>
            </Container>
        </div>
    );
} 